import React, { useMemo, useState } from "react";

// Modale de choix d'un template — utilisée dans la grille du programme, une
// dropdown de 50+ items serait ingérable. Alignée sur l'ergo de la modale
// d'import (recherche + pills dossiers + liste), mais SANS la logique "déjà
// présent chez l'élève" : dans le contexte programme le même template peut
// être placé sur plusieurs cellules.
export default function TemplatePickerModal({
	coachTemplates,
	coachFolders,
	coachAssoc,
	currentTemplateId,   // pour matérialiser le choix actuel dans la liste
	onPick,               // (template) => void — la modale se ferme via onClose au parent
	onCreateNew,          // () => void — bouton "Créer un nouveau template"
	onClose,
}) {
	const [q, setQ] = useState("");
	const [activeFolderKey, setActiveFolderKey] = useState(null);

	const activeFolders = useMemo(() => {
		const arr = (coachFolders || []).filter((f) => !(f.Supprime ?? f.supprime));
		arr.sort((a, b) => (a.NomDossier ?? a.nomDossier ?? "").localeCompare(b.NomDossier ?? b.nomDossier ?? ""));
		return arr;
	}, [coachFolders]);

	// Index folderKey → Set(templateKey) pour filtre O(1).
	const folderTemplateIndex = useMemo(() => {
		const m = new Map();
		(coachAssoc || []).forEach((a) => {
			if (a.Supprime ?? a.supprime) return;
			const fk = a.FolderKey ?? a.folderKey;
			const tk = a.TemplateKey ?? a.templateKey;
			if (fk == null || tk == null) return;
			if (!m.has(fk)) m.set(fk, new Set());
			m.get(fk).add(tk);
		});
		return m;
	}, [coachAssoc]);
	const folderCount = (folderKey) => (folderTemplateIndex.get(folderKey)?.size ?? 0);

	const list = useMemo(() => {
		const term = q.trim().toLowerCase();
		const folderTplKeys = activeFolderKey != null ? folderTemplateIndex.get(activeFolderKey) : null;
		return (coachTemplates || [])
			.filter((t) => {
				if (!folderTplKeys) return true;
				const key = t.Key ?? t.key;
				return folderTplKeys.has(key);
			})
			.filter((t) => !term || (t.nom ?? t.Nom ?? "").toLowerCase().includes(term));
	}, [coachTemplates, q, activeFolderKey, folderTemplateIndex]);

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			<div
				className="mens-modal"
				onClick={(e) => e.stopPropagation()}
				style={{
					// Même dimensionnement que la modale d'import pour cohérence visuelle.
					// Fixe pour éviter le "flash" de redimensionnement en changeant de filtre.
					width: "90vw",
					height: "80vh",
					maxWidth: "none",
					maxHeight: "none",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
				}}
			>
				<div className="mens-modal-header">
					<h3>Choisir un modèle</h3>
					<button className="mens-modal-close" onClick={onClose}>×</button>
				</div>

				{activeFolders.length > 0 && (
					<div className="import-modal-folders">
						<button
							type="button"
							className={`import-modal-folder-pill ${activeFolderKey === null ? "active" : ""}`}
							onClick={() => setActiveFolderKey(null)}
						>
							Tous
							<span className="import-modal-folder-count">{(coachTemplates || []).length}</span>
						</button>
						{activeFolders.map((f) => {
							const fk = f.Key ?? f.key;
							const nom = f.NomDossier ?? f.nomDossier ?? "Dossier";
							const count = folderCount(fk);
							return (
								<button
									key={fk}
									type="button"
									className={`import-modal-folder-pill ${activeFolderKey === fk ? "active" : ""}`}
									onClick={() => setActiveFolderKey(fk)}
									title={nom}
								>
									<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
										<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
									</svg>
									<span className="import-modal-folder-name">{nom}</span>
									<span className="import-modal-folder-count">{count}</span>
								</button>
							);
						})}
					</div>
				)}

				<div className="editor-panel-exercises-search" style={{ marginTop: 8 }}>
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<circle cx="11" cy="11" r="8" />
						<line x1="21" y1="21" x2="16.65" y2="16.65" />
					</svg>
					<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un modèle" autoFocus />
					{q && <button className="editor-panel-exercises-clear" onClick={() => setQ("")}>×</button>}
				</div>

				<div
					className="import-modal-list"
					style={{ flex: 1, minHeight: 200, overflowY: "auto", marginTop: 12 }}
				>
					{list.length === 0 ? (
						<div className="coach-exercises-library-empty">
							{q.trim()
								? "Aucun modèle ne correspond à la recherche"
								: activeFolderKey != null
									? "Ce dossier est vide"
									: "Aucun modèle — utilise le bouton ci-dessous pour en créer un."}
						</div>
					) : list.map((tpl) => {
						const tplId = tpl.Id ?? tpl.id;
						const nom = tpl.Nom ?? tpl.nom ?? "Sans nom";
						const exos = tpl.ListExercice ?? tpl.listExercice ?? tpl.exercices ?? [];
						const isCurrent = tplId === currentTemplateId;
						return (
							<button
								key={tplId}
								type="button"
								onClick={() => onPick(tpl)}
								style={{
									display: "flex", alignItems: "center", gap: 10, width: "100%",
									padding: "10px 12px", marginBottom: 6, borderRadius: 8,
									border: isCurrent ? "1px solid rgba(48,164,154,0.5)" : "1px solid var(--line-soft)",
									background: isCurrent ? "rgba(48,164,154,0.10)" : "transparent",
									color: "var(--ink)", cursor: "pointer", textAlign: "left",
								}}
							>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{nom}</div>
									<div style={{ fontSize: 11, color: "var(--ink-soft)" }}>
										{exos.length} exo{exos.length > 1 ? "s" : ""}
									</div>
								</div>
								<span style={{ fontSize: 12, fontWeight: 600, color: isCurrent ? "#1a6a63" : "#30a49a" }}>
									{isCurrent ? "Sélectionné" : "Choisir →"}
								</span>
							</button>
						);
					})}
				</div>

				<div className="mens-modal-actions">
					<button className="mens-btn ghost" onClick={onClose}>Annuler</button>
					{/* Bouton "création à la volée" — délègue au parent pour la vraie
					    navigation (ferme la modale programme + ouvre l'éditeur template).
					    Le coach revient ensuite manuellement au programme. */}
					{onCreateNew && (
						<button className="mens-btn primary" onClick={onCreateNew}>
							+ Créer un nouveau modèle
						</button>
					)}
				</div>
			</div>
		</div>
	);
}
